import React, { useState } from 'react';
import styled from '@emotion/styled';
import { css, useTheme } from '@emotion/react';
import { RichTextBlock } from 'prismic-reactjs';
import AnimateHeight from 'react-animate-height';

import DownChevron from './assets/downChevron.inline.svg';

import { RichText } from 'components/Prismic';
import { filterProps } from 'utils/helpers';

interface IFaqItem {
  question: string;
  answer: RichTextBlock[];
}

const Container = styled.div`
  ${({ theme: { colors, mq } }) => css`
    display: grid;
    grid-template-columns: auto;
    column-gap: 2.25rem;
    align-items: center;
    border-radius: 1rem;
    background-color: ${colors.white};
    cursor: pointer;
    padding: 2rem 4rem;

    ${mq.md} {
      gap: 1.5rem;
      padding-bottom: 0.5rem;
    }
  `}
`;

const Question = styled.h4`
  ${({ theme: { colors, mq, tp } }) => css`
    display: inline-grid;
    grid-template-columns: 1fr auto;
    grid-auto-flow: column;
    column-gap: 1.5rem;
    align-items: center;
    margin: 0;
    line-height: 2.375;
    letter-spacing: -0.04em;
    color: ${colors.black};
    font-size: 3rem;

    ${tp.useFont(fonts => fonts.Switzer.regular)};

    ${mq.md} {
      line-height: 1.5;
      letter-spacing: initial;
      font-size: 2rem;
    }
  `}
`;

const Answer = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    flex-direction: column;
    padding-bottom: 1rem;

    & > p {
      line-height: 1.65;
      color: ${colors.black}CC;
      font-size: 2rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }
  `}
`;

const Chevron = styled(DownChevron, filterProps('isOpen'))<{ isOpen: boolean }>`
  ${({ theme: { durations }, isOpen }) => css`
    transform: rotate(${isOpen ? 180 : 0}deg);
    transition: transform ${durations.regular} ease-in-out;
    width: 2.625rem;
    height: 1.75rem;
  `}
`;

const FaqItem: React.FC<IFaqItem> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { durations } = useTheme();

  return (
    <Container key={question} onClick={() => setIsOpen(!isOpen)}>
      <Question>
        {question} <Chevron isOpen={isOpen} />
      </Question>

      <AnimateHeight height={isOpen ? 'auto' : 0} duration={Number(durations.regular.slice(0, -2))}>
        <Answer render={answer} />
      </AnimateHeight>
    </Container>
  );
};

export default FaqItem;
